import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { PermissionGate } from '../../components/PermissionGate';
import { alertKey } from '../../components/wiseguy/alertStore';
import { useWiseguyAlerts } from '../../hooks/useWiseguyAlerts';
import { usePermissions } from '../../hooks/usePermissions';
import type { Bias, ContractType, WiseguyAlert } from '../../types/wiseguy';
import { useHubPane } from '../hubPrefs';
import { mobilePrefsStore, usePaneParams } from '../mobilePrefsStore';
import { useLongPressCopy } from '../useLongPressCopy';
import { MobileWsjFeed } from './MobileWsjFeed';

const MOBILE_ALERT_CARD_HEIGHT = 132;

type AlertsHub = 'tape' | 'wsj';

function formatTime(dateStr: string): string {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function AlertCard({
  alert,
  flash,
  longPress,
}: {
  alert: WiseguyAlert;
  flash: 'new' | 'update' | null;
  longPress: ReturnType<typeof useLongPressCopy>;
}): ReactElement {
  const biasColor =
    alert.bias === 'Bullish'
      ? 'text-bull-blue'
      : alert.bias === 'Bearish'
        ? 'text-bear-red'
        : 'text-gray-400';
  const biasBg =
    alert.bias === 'Bullish'
      ? 'bg-bull-blue'
      : alert.bias === 'Bearish'
        ? 'bg-bear-red'
        : 'bg-gray-400';
  const typeBadge =
    alert.contract_type === 'Call'
      ? 'bg-bull-blue/20 text-bull-blue'
      : 'bg-bear-red/20 text-bear-red';

  const flashClass =
    flash === 'new'
      ? 'ring-1 ring-bull-blue/60'
      : flash === 'update'
        ? 'ring-1 ring-accent-yellow/50'
        : '';

  return (
    <article
      className={`bg-dark-surface rounded-lg p-3 border border-dark-border ${flashClass}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full shrink-0 ${biasBg}`} />
          <span
            className="text-base font-bold text-white truncate"
            {...longPress.handlersFor(() => {
              void navigator.clipboard?.writeText(alert.symbol);
            })}
          >
            {alert.symbol}
          </span>
          <span className={`text-xs px-1.5 py-0.5 rounded shrink-0 ${typeBadge}`}>
            {alert.contract_type}
          </span>
          {alert.top_bet && <span className="text-xs shrink-0" title="Top bet">🏆</span>}
          {alert.top_sweep && <span className="text-xs shrink-0" title="Top sweep">🧹</span>}
        </div>
        <span className="text-xs text-gray-500 shrink-0 ml-2">
          {formatTime(alert.received_on)}
        </span>
      </div>

      <div className="flex items-center gap-3 text-sm flex-wrap">
        <span className="text-gray-300">${alert.strike}</span>
        <span className="text-gray-500">{alert.expiry}</span>
        {alert.time_to_expiry_days !== undefined && (
          <span className="text-gray-500">{alert.time_to_expiry_days}d</span>
        )}
      </div>

      <div className="flex items-center gap-3 mt-2 text-xs text-gray-400 flex-wrap">
        <span className="font-medium text-white">{alert.bet_display}</span>
        {alert.multiplier && <span>{alert.multiplier}x</span>}
        <span className={biasColor}>{alert.bias}</span>
        {alert.alert_type && <span className="text-gray-500">{alert.alert_type}</span>}
      </div>

      {alert.comment && (
        <div className="mt-2 text-xs text-gray-500 italic border-l-2 border-dark-border pl-2">
          {alert.comment}
        </div>
      )}
    </article>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-colors min-h-[36px] ${
        active
          ? 'bg-bull-blue text-black'
          : 'bg-dark-surface-light text-gray-400 border border-dark-border'
      }`}
    >
      {label}
    </button>
  );
}

export interface AlertFilters {
  symbolFilter: string;
  bias: Bias | '';
  contractType: ContractType | '';
  topBetOnly: boolean;
}

const ALERT_FILTER_DEFAULTS: AlertFilters = {
  symbolFilter: '',
  bias: '',
  contractType: '',
  topBetOnly: false,
};

const BIASES: readonly string[] = ['Bullish', 'Bearish', 'Neutral'];
const CONTRACT_TYPES: readonly string[] = ['Call', 'Put'];

/**
 * Validate stored tape filters; anything unexpected falls back to a default.
 * Exported alongside the component so it stays unit-testable without a
 * live-query/permissions harness (see MobileAlertsFilters.test.ts).
 */
// eslint-disable-next-line react-refresh/only-export-components
export function readAlertFilters(raw: unknown): AlertFilters {
  if (typeof raw !== 'object' || raw === null) return ALERT_FILTER_DEFAULTS;
  const r = raw as Record<string, unknown>;
  return {
    symbolFilter:
      typeof r.symbolFilter === 'string' ? r.symbolFilter : '',
    bias:
      typeof r.bias === 'string' && BIASES.includes(r.bias)
        ? (r.bias as Bias)
        : '',
    contractType:
      typeof r.contractType === 'string' && CONTRACT_TYPES.includes(r.contractType)
        ? (r.contractType as ContractType)
        : '',
    topBetOnly: typeof r.topBetOnly === 'boolean' ? r.topBetOnly : false,
  };
}

/**
 * Wiseguy options tape (QF data path).
 * Display filters only; product membership is server/channel floor.
 */
function MobileAlertsTape(): ReactElement {
  const { hasToolAccess } = usePermissions();
  const entitled = hasToolAccess('wiseguy-alerts');

  const storedFilters = usePaneParams('mobileAlerts');
  const seeded = readAlertFilters(storedFilters);

  const [symbolFilter, setSymbolFilterState] = useState(seeded.symbolFilter);
  const [bias, setBiasState] = useState<Bias | ''>(seeded.bias);
  const [contractType, setContractTypeState] = useState<ContractType | ''>(
    seeded.contractType,
  );
  const [topBetOnly, setTopBetOnlyState] = useState(seeded.topBetOnly);

  // Prefs can land after first paint. Re-seed once they do, using the same
  // "adjust state during render" idiom as useHubPane (see hubPrefs.ts):
  // comparing + updating synchronously in the render body avoids the
  // react-hooks/set-state-in-effect warning an effect-based re-seed would
  // trip, and applies the update on the same render prefs arrive rather than
  // one render late.
  const seededKey = JSON.stringify(seeded);
  const [appliedSeedKey, setAppliedSeedKey] = useState(seededKey);
  if (seededKey !== appliedSeedKey) {
    setAppliedSeedKey(seededKey);
    setSymbolFilterState(seeded.symbolFilter);
    setBiasState(seeded.bias);
    setContractTypeState(seeded.contractType);
    setTopBetOnlyState(seeded.topBetOnly);
  }

  const persist = useCallback((partial: Partial<AlertFilters>) => {
    mobilePrefsStore.setPaneParams('mobileAlerts', partial);
  }, []);

  const setSymbolFilter = useCallback(
    (v: string) => {
      setSymbolFilterState(v);
      persist({ symbolFilter: v });
    },
    [persist],
  );
  const setBias = useCallback(
    (v: Bias | '') => {
      setBiasState(v);
      persist({ bias: v });
    },
    [persist],
  );
  const setContractType = useCallback(
    (v: ContractType | '') => {
      setContractTypeState(v);
      persist({ contractType: v });
    },
    [persist],
  );
  const setTopBetOnly = useCallback(
    (v: boolean) => {
      setTopBetOnlyState(v);
      persist({ topBetOnly: v });
    },
    [persist],
  );

  const {
    alerts,
    isLoading,
    isFetching,
    error,
    truncated,
    scopeLabel,
    mode,
    refetch,
    recentlyNewKeys,
    recentlyUpdatedKeys,
  } = useWiseguyAlerts({
    symbolFilter,
    instanceId: 'mobile-alerts',
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const longPress = useLongPressCopy();

  const visible = useMemo(() => {
    return alerts.filter((a) => {
      if (bias && a.bias !== bias) return false;
      if (contractType && a.contract_type !== contractType) return false;
      if (topBetOnly && !a.top_bet) return false;
      const symbols = symbolFilter
        .split(',')
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean);
      if (mode === 'session' && symbols.length > 1) {
        if (!symbols.includes(a.symbol.toUpperCase())) return false;
      }
      return true;
    });
  }, [alerts, bias, contractType, topBetOnly, symbolFilter, mode]);

  const cardVirtualizer = useVirtualizer({
    count: visible.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => MOBILE_ALERT_CARD_HEIGHT,
    overscan: 8,
    getItemKey: (index) => alertKey(visible[index]),
  });

  if (!entitled) {
    return (
      <div className="p-4 text-center text-gray-400 text-sm">
        Wiseguy Alerts requires an entitled FZN Pro plan.
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="shrink-0 border-b border-dark-border bg-dark-bg">
        <div className="flex items-center justify-between gap-2 px-4 pt-2 pb-2">
          <p className="text-[11px] text-gray-500 truncate min-w-0">
            {scopeLabel}
            {mode === 'search' ? ' · search' : ' · session'}
            {truncated ? ' · truncated' : ''}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-400 disabled:opacity-50"
            aria-label="Refresh alerts"
          >
            <svg
              className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </button>
        </div>

        <div className="px-4 pb-2">
          <input
            type="text"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Symbol search (e.g. NVDA)"
            value={symbolFilter}
            onChange={(e) => setSymbolFilter(e.target.value.toUpperCase())}
            className="w-full min-h-[44px] px-3 rounded-lg border border-dark-border bg-dark-surface text-white placeholder:text-gray-600"
            aria-label="Filter by symbol"
          />
        </div>

        <div className="flex gap-2 px-4 pb-3 overflow-x-auto">
          <FilterChip
            label="Bullish"
            active={bias === 'Bullish'}
            onClick={() => setBias(bias === 'Bullish' ? '' : 'Bullish')}
          />
          <FilterChip
            label="Bearish"
            active={bias === 'Bearish'}
            onClick={() => setBias(bias === 'Bearish' ? '' : 'Bearish')}
          />
          <FilterChip
            label="Calls"
            active={contractType === 'Call'}
            onClick={() =>
              setContractType(contractType === 'Call' ? '' : 'Call')
            }
          />
          <FilterChip
            label="Puts"
            active={contractType === 'Put'}
            onClick={() =>
              setContractType(contractType === 'Put' ? '' : 'Put')
            }
          />
          <FilterChip
            label="Top Bets"
            active={topBetOnly}
            onClick={() => setTopBetOnly(!topBetOnly)}
          />
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 min-h-0 overflow-y-auto p-4 overscroll-contain"
      >
        {error && (
          <div className="text-sm text-bear-red border border-bear-red/30 rounded-lg p-3 bg-bear-red/10 mb-3">
            {error.message}
            <button
              type="button"
              className="block mt-2 text-bull-blue underline"
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        )}

        {isLoading && visible.length === 0 ? (
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-dark-surface rounded-lg p-3 border border-dark-border animate-pulse"
              >
                <div className="h-4 bg-dark-surface-light rounded w-24 mb-2" />
                <div className="h-3 bg-dark-surface-light rounded w-40 mb-2" />
                <div className="h-3 bg-dark-surface-light rounded w-32" />
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No alerts match your filters
          </div>
        ) : (
          <div
            className="relative w-full"
            style={{ height: cardVirtualizer.getTotalSize() }}
          >
            {cardVirtualizer.getVirtualItems().map((virtualRow) => {
              const alert = visible[virtualRow.index];
              const key = alertKey(alert);
              const flash: 'new' | 'update' | null = recentlyNewKeys.has(key)
                ? 'new'
                : recentlyUpdatedKeys.has(key)
                  ? 'update'
                  : null;
              return (
                <div
                  key={key}
                  data-index={virtualRow.index}
                  ref={cardVirtualizer.measureElement}
                  className="absolute left-0 right-0 pb-3"
                  style={{ transform: `translateY(${virtualRow.start}px)` }}
                >
                  <AlertCard alert={alert} flash={flash} longPress={longPress} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const ALERTS_HUBS = ['tape', 'wsj'] as const satisfies readonly AlertsHub[];

/**
 * Alerts hub: options tape + WSJ desk feed (not under Audio).
 */
export function MobileAlertsTab(): ReactElement {
  const [hub, setHub] = useHubPane<AlertsHub>('alertsHub', ALERTS_HUBS, 'tape');

  return (
    <div className="sr-mobile-hub">
      <div className="px-4 pt-3 pb-1 shrink-0">
        <h1 className="text-lg font-bold text-white">Alerts</h1>
      </div>

      <div className="sr-mobile-hub-chips" role="tablist" aria-label="Alerts hub">
        <button
          type="button"
          role="tab"
          className="sr-mobile-hub-chip"
          aria-selected={hub === 'tape'}
          onClick={() => setHub('tape')}
        >
          Tape
        </button>
        <button
          type="button"
          role="tab"
          className="sr-mobile-hub-chip"
          aria-selected={hub === 'wsj'}
          onClick={() => setHub('wsj')}
        >
          WSJ
        </button>
      </div>

      <div className="sr-mobile-hub-stage sr-mobile-hub-stage--widget">
        {hub === 'tape' && <MobileAlertsTape />}
        {hub === 'wsj' && (
          <PermissionGate toolType="wsj-feed">
            <MobileWsjFeed />
          </PermissionGate>
        )}
      </div>
    </div>
  );
}
